import {Service} from "crqlar-react-common/dist/models/service/service.model";
import {Box, Stack, Typography, useTheme} from "@mui/material";
import React from "react";
import {CommonThemeColors} from "crqlar-react-common/dist/components/theme";
import {
    GuestCount,
    Reservation
} from "crqlar-react-common/dist/models/reservation/reservation.model";
import CheckIcon from "@mui/icons-material/Check";
import {
    AgeCategory,
    AgeCategoryType
} from "crqlar-react-common/dist/models/ageCategory/ageCategory.model";
import {MbscCalendarEventData} from "@mobiscroll/react";
import {hexToRgba} from "crqlar-react-common/dist/utils/color.util";
import {useTranslation} from "react-i18next";
import {
    AvailabilityCalculationResult
} from "crqlar-react-common/dist/models/availability/availabilityCalculationResult.model";
import {
    AvailabilityType
} from "crqlar-react-common/dist/models/availability/availability.model";

interface Props {
    service?: Service
    ageCategories?: AgeCategory[]
    event: MbscCalendarEventData

}

export const ServiceEventPreview = ({
                                        service,
                                        ageCategories,
                                        event
                                    }: Props) => {
    const theme = useTheme();

    const availability = event.original?.availability as AvailabilityCalculationResult
    const maxCapacity: number | undefined = event.original?.maxCapacity ?? undefined
    const reservations = event.original?.reservations ?? [] as Reservation[]
    const isDisabled = availability.type === AvailabilityType.CLOSED;

    const backgroundColor = isDisabled
        ? hexToRgba(service?.color?.background ?? CommonThemeColors.sys.success.background, 0.3)
        : service?.color?.background ?? CommonThemeColors.sys.success.background;

    const textColor = isDisabled
        ? CommonThemeColors.sys.availability.boldRed
        : service?.color?.text || CommonThemeColors.black;


    const icon = <CheckIcon sx={{color: textColor, fontSize: "20px"}}/>;
    const fontSizeTop = theme.typography.h6.fontSize as string;


    const guestCounts = (reservations as Reservation[])
        .flatMap(r => r.guestCounts || [])
        .filter(Boolean) as GuestCount[];
    const totalGuestCount = guestCounts.reduce((sum, gc) => sum + (gc.count || 0), 0);


    const adultAgeCategories =
        ageCategories?.filter(ac => ac.type === AgeCategoryType.ADULT).map(ac => ac.id) || [];
    const nonAdultAgeCategories =
        ageCategories?.filter(ac => ac.type !== AgeCategoryType.ADULT).map(ac => ac.id) || [];


    const adultGuestCount =
        guestCounts
            .filter(gc => adultAgeCategories.includes(gc.ageCategoryId))
            .reduce((sum, gc) => sum + (gc.count || 0), 0);
    const nonAdultGuestCount =
        guestCounts
            .filter(gc => nonAdultAgeCategories.includes(gc.ageCategoryId))
            .reduce((sum, gc) => sum + (gc.count || 0), 0);

    const {i18n} = useTranslation()
    const displayName =
        service?.nameLocalized?.[i18n.language] ||
        service?.name ||
        availability?.name ||
        "Service";

    return (
        <Stack
            sx={{
                borderRadius: "5px",
                padding: "2px 8px",
                backgroundColor: backgroundColor,
                color: textColor,
                backgroundImage: isDisabled
                    ? `repeating-linear-gradient(
                45deg,
                transparent,
                transparent 10px,
                ${hexToRgba(textColor, 0.1)} 10px,
                ${hexToRgba(textColor, 0.1)} 20px
              )`
                    : 'none',
                gap: "5px",
                justifyContent: "space-between",
                alignItems: "flex-start",
                width: "100%",
                height: "100%",
                overflow: "hidden",
                border: "none",
            }}
        >
            <Stack
                direction={"row"}
                sx={{
                    justifyContent: "space-between",
                    gap: "5px",
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                }}
            >
                <Typography
                    fontSize={fontSizeTop}
                    whiteSpace={"nowrap"}
                >
                    {displayName}
                </Typography>

                <Typography
                    fontSize={fontSizeTop}
                    whiteSpace={"nowrap"}
                    sx={{flex: "auto", textAlign: "right"}}
                >
                    {`${totalGuestCount} (${adultGuestCount}/${nonAdultGuestCount})`}
                </Typography>

                <Box ml={"auto"}>
                    {icon}
                </Box>
            </Stack>

            <Stack
                direction={"row"}
                sx={{
                    gap: "5px",
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: "100%",
                }}
            >
                <Box sx={{display: "flex", alignItems: "center", gap: "2px"}}/>

                <Typography
                    fontSize={fontSizeTop}
                    whiteSpace={"nowrap"}
                    sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        minWidth: 0,
                        flex: "auto",
                        fontSize: "14px",
                        ml: "-0.35em",
                    }}
                >
                    {maxCapacity ? `${totalGuestCount}/${maxCapacity}` : `${totalGuestCount}`}
                </Typography>
            </Stack>
        </Stack>
    );
};

export default ServiceEventPreview;
